import { isPlatformServer } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { GalleryDto } from '@picflow/types';
import { firstValueFrom } from 'rxjs';
import { AssetService } from '../../../services/asset.service';
import { GuestAuthService } from '../../../services/guest-auth.service';
import { GuestService } from '../../../services/guest.service';
import { UserService } from '../../../services/user.service';
import { WatcherService } from '../../../services/watcher.service';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';

@Component({
  selector: 'app-unsubscribed-from-gallery-page',
  templateUrl: './unsubscribed-from-gallery-page.component.html',
  styleUrls: ['./unsubscribed-from-gallery-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SvgComponent, ButtonDirective, RouterLink, GalleryTeaserUrlPipe],
})
export class UnsubscribedFromGalleryPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private watcherService = inject(WatcherService);
  private userService = inject(UserService);
  private guestService = inject(GuestService);
  private guestAuthService = inject(GuestAuthService);
  private assetService = inject(AssetService);
  private platformId = inject(PLATFORM_ID);
  private check = inject(ChangeDetectorRef);
  gallery?: GalleryDto;
  galleryId: string;

  async ngOnInit() {
    this.gallery = this.route.snapshot.data.gallery; // this.gallery might not be there if the gallery has a password
    this.galleryId = this.route.snapshot.params.galleryId;
    if (isPlatformServer(this.platformId)) return;
    const user = await firstValueFrom(this.userService.getUser());
    const guest = await this.guestService.getGuest();

    if (!user && !guest) {
      if (this.route.snapshot.queryParams.email) {
        await this.guestAuthService.authenticateWithEmail(
          'Unknown',
          this.route.snapshot.queryParams.email,
          true,
          undefined
        );
      }

      const guest = await this.guestService.getGuest();
      if (!guest) {
        const resp = await this.guestAuthService.authenticateWithPopup(this.gallery);
        if (!resp) return false;
      }
    }

    const [subscriptions, assets] = await Promise.all([
      firstValueFrom(this.watcherService.mySubscriptions$),
      this.assetService.getAssetsForGallery(this.galleryId),
    ]);
    await Promise.all([
      ...assets
        .filter(img => !!subscriptions.find(s => s.assetId === img.id))
        .map(img => this.watcherService.unWatchAsset$.next(img.id)),
      ...subscriptions
        .filter(sub => sub.galleryId === this.galleryId)
        .map(sub => this.watcherService.unWatchGallery$.next(sub.galleryId)),
    ]);
  }
}
